<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Security Guards"
      subtitle="Manage society security guards and their access"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openGuardAdd"
      @edit="openGuardEdit($event)"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="securityguards"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Custom Cells -->
      <template #col-profile_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img
              v-if="item.profile_url && item.profile_url !== 'null'"
              :src="API_BASE_URL + '/uploads/' + item.profile_url"
              class="p-avatar-img"
              style="cursor: pointer"
              @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.profile_url, item.name)"
            />
            <div
              v-else
              class="p-avatar-placeholder"
              :style="{ background: primaryColor + '20', color: primaryColor }"
            >
              {{
                item.name && item.name !== "null"
                  ? item.name.charAt(0).toUpperCase()
                  : "G"
              }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{
              item.name && item.name !== "null" ? item.name : "—"
            }}</span>
            <!-- <span class="p-id" v-if="item.id">ID: #SG{{ item.id }}</span> -->
          </div>
        </div>
      </template>

      <template #col-mobile_no="{ item }">
        <div class="mobile-cell">
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span
            >{{
              item.country_code
                ? (item.country_code.toString().startsWith("+")
                    ? item.country_code
                    : "+" + item.country_code) + " "
                : ""
            }}{{ item.mobile_no }}</span
          >
        </div>
      </template>

      <template #col-email="{ item }">
        <div class="email-cell" v-if="item.email">
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-email-outline</v-icon
          >
          <span>{{ item.email }}</span>
        </div>
        <span class="dash" v-else>—</span>
      </template>

      <template #col-shift="{ item }">
        <div v-if="item.shift_from && item.shift_to" class="shift-cell">
          <v-icon size="14" color="#94A3B8" class="mr-1">mdi-clock-outline</v-icon>
          <span>{{ formatTime12(item.shift_from) }} – {{ formatTime12(item.shift_to) }}</span>
        </div>
        <span class="dash" v-else>—</span>
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ────────────────── -->
    <PremiumModal
      v-model="guardDialog"
      :title="(guard.id ? 'Edit' : 'Add') + ' Security Guard'"
      :loading="saveLoading"
      submit-text="Submit"
      cancel-text="Cancel"
      max-width="500"
      @submit="guard.id ? updateGuard() : saveGuard()"
      @cancel="closeGuardDialog"
      @close="closeGuardDialog"
    >
      <!-- Avatar uploader -->
      <div class="d-flex justify-center mb-6">
        <input
          type="file"
          ref="guard_pic"
          @change="changeFile($event)"
          accept="image/*"
          hidden
        />
        <div class="avatar-uploader-container">
          <v-avatar size="100" class="elevation-3 guard-avatar">
            <img
              v-if="previewImage || guard.profile_url"
              :src="previewImage || API_BASE_URL + '/uploads/' + guard.profile_url"
              style="object-fit: contain"
            />
            <span
              v-else
              class="text-h3 font-weight-bold"
              :style="`color:${primaryColor}`"
            >
              {{ guard.name ? guard.name.charAt(0).toUpperCase() : "G" }}
            </span>
          </v-avatar>
          <v-btn fab small depressed @click="openFileExplorer" class="avatar-edit-btn" color="var(--brand)">
            <v-icon small color="white">mdi-camera</v-icon>
          </v-btn>
        </div>
      </div>

      <v-row dense>
        <!-- Name -->
        <v-col cols="12">
          <PremiumInput
            v-model="guard.name"
            label="Full Name *"
            placeholder="Enter guard's full name"
            icon="mdi-account-outline"
            v-validate="'required'"
            name="guard_name"
            data-vv-as="Name"
            :error="errors.first('guard_name') || serverSideErrors.name"
          />
        </v-col>

        <!-- Mobile -->
        <v-col cols="12">
          <div class="field-label mb-2">Mobile Number *</div>
          <div class="phone-row">
            <div class="country-code-wrap">
              <vue-country-code
                @onSelect="onSelect"
                class="custom-country-code-select-full"
                :defaultCountry="guard.countryIso || 'IN'"
              />
            </div>
            <div class="mobile-input-wrap">
              <PremiumInput
                v-model="guard.mobile_no"
                label=""
                placeholder="Enter mobile number"
                icon="mdi-phone-outline"
                v-validate="'required'"
                name="guard_mobile"
                data-vv-as="Mobile"
                :error="errors.first('guard_mobile') || serverSideErrors.mobileNo"
              />
            </div>
          </div>
        </v-col>

        <!-- Email -->
        <v-col cols="12">
          <PremiumInput
            v-model="guard.email"
            label="Email Address"
            placeholder="Enter email (optional)"
            icon="mdi-email-outline"
            name="guard_email"
            :error="serverSideErrors.email"
          />
        </v-col>

        <!-- Shift Time -->
        <v-col cols="12">
          <div class="field-label mb-2">Shift Time</div>
          <div class="shift-time-row">
            <div class="shift-time-field" @click="openShiftPicker('from')">
              <v-icon size="16" color="#94A3B8">mdi-clock-start</v-icon>
              <span class="shift-time-val" :class="{ 'has-val': guard.shift_from }">
                {{ guard.shift_from ? formatTime12(guard.shift_from) : 'From' }}
              </span>
              <button v-if="guard.shift_from" class="shift-clear-btn" type="button" @click.stop="guard.shift_from = null">
                <v-icon size="13">mdi-close</v-icon>
              </button>
            </div>
            <v-icon size="18" color="#CBD5E1">mdi-arrow-right</v-icon>
            <div class="shift-time-field" @click="openShiftPicker('to')">
              <v-icon size="16" color="#94A3B8">mdi-clock-end</v-icon>
              <span class="shift-time-val" :class="{ 'has-val': guard.shift_to }">
                {{ guard.shift_to ? formatTime12(guard.shift_to) : 'To' }}
              </span>
              <button v-if="guard.shift_to" class="shift-clear-btn" type="button" @click.stop="guard.shift_to = null">
                <v-icon size="13">mdi-close</v-icon>
              </button>
            </div>
          </div>
        </v-col>
      </v-row>
    </PremiumModal>

    <!-- ── Shift Time Picker Popup ────────────────── -->
    <v-dialog
      v-model="shiftPickerOpen"
      max-width="320"
      persistent
      :overlay-opacity="0.3"
      content-class="shift-time-dialog"
    >
      <v-card style="border-radius: 16px; overflow: hidden;">
        <!-- Header -->
        <div class="shift-dialog-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <v-icon size="18" :color="primaryColor">mdi-clock-outline</v-icon>
            <span class="shift-dialog-title">
              {{ shiftPickerTarget === 'from' ? 'Select Start Time' : 'Select End Time' }}
            </span>
          </div>
          <button class="shift-picker-close" type="button" @click="shiftPickerOpen = false">
            <v-icon size="16" :color="isDark ? '#94a3b8' : '#374151'">mdi-close</v-icon>
          </button>
        </div>

        <!-- Time Picker -->
        <v-time-picker
          :key="shiftPickerTarget"
          v-model="shiftPickerValue"
          full-width
          scrollable
          ampm-in-title
          :color="primaryColor"
          :dark="isDark"
          class="shift-dialog-picker"
        />

        <!-- Actions -->
        <div class="shift-dialog-actions">
          <v-btn text small  class="mr-2 btn-premium-cancel" @click="shiftPickerOpen = false">
            Cancel
          </v-btn>
          <v-btn
            depressed small
             class="btn-premium-primary ml-3"
          :style="{ '--btn-color': btnColor }"
            style="text-transform:none; font-weight:700; border-radius:8px; letter-spacing:0;"
            @click="confirmShiftTime"
          >
            Confirm
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Security Guard"
      message="Are you sure you want to remove this security guard? Their login access will be revoked immediately."
      confirm-text="Yes, Remove Guard"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteSecurityGuard"
    />

    <!-- ── Media Viewer ────────────────────── -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "SecurityGuardList",
  mixins: [filterMixin],
  components: { PremiumTable, PremiumModal, PremiumInput, ConfirmModal, MediaViewer },

  data() {
    return {
      search: "",
      securityguards: [],
      totalRecords: 0,
      loading: false,
      saveLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      guardDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      user: {},
      // Guard form
      guard: {
        id: null,
        name: "",
        mobile_no: "",
        email: "",
        country_code: "+91",
        countryIso: "IN",
        profile_url: null,
        shift_from: null,
        shift_to: null,
      },
      shiftFromMenu: false,
      shiftToMenu: false,
      shiftPickerOpen: false,
      shiftPickerTarget: 'from', // 'from' | 'to'
      shiftPickerValue: null,
      profile_pic: null,
      previewImage: null,
      serverSideErrors: {},
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor", "isDark"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        {
          key: "profile_url",
          label: this.$t("securityguard.headers.name") || "Guard Profile",
          width: "240px",
        },
        {
          key: "mobile_no",
          label: this.$t("securityguard.headers.mobile_no") || "Contact",
        },
        {
          key: "email",
          label: this.$t("securityguard.headers.email") || "Email",
        },
        {
          key: "shift",
          label: "Shift Time",
          width: "160px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getSecurityGuards();
    },
    itemsPerPage() {
      this.getSecurityGuards();
    },
    search() {
      this.page = 1;
      this.getSecurityGuards();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getSecurityGuards();
    },

    onSelect(event) {
      this.guard.country_code = "+" + event.dialCode;
      this.guard.countryIso = event.iso2;
    },

    openFileExplorer() {
      this.$refs.guard_pic.click();
    },

    changeFile(e) {
      this.profile_pic = e.target.files[0];
      const input = this.$refs.guard_pic;
      const file = input.files;
      if (file && file[0]) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.previewImage = ev.target.result;
        };
        reader.readAsDataURL(file[0]);
      }
    },

    openShiftPicker(target) {
      this.shiftPickerTarget = target;
      this.shiftPickerValue  = target === 'from' ? this.guard.shift_from : this.guard.shift_to;
      this.shiftPickerOpen   = true;
    },

    confirmShiftTime() {
      if (this.shiftPickerTarget === 'from') {
        this.guard.shift_from = this.shiftPickerValue;
      } else {
        this.guard.shift_to = this.shiftPickerValue;
      }
      this.shiftPickerOpen  = false;
      this.shiftPickerValue = null;
    },

    openGuardAdd() {
      this.guard = {
        id: null, name: "", mobile_no: "", email: "",
        country_code: "+91", countryIso: "IN",
        profile_url: null, shift_from: null, shift_to: null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.shiftPickerOpen = false;
      this.shiftPickerValue = null;
      this.$validator.reset();
      this.serverSideErrors = {};
      this.guardDialog = true;
    },

    openGuardEdit(item) {
      this.guard = {
        id: item.id, name: item.name || "", mobile_no: item.mobile_no || "",
        email: item.email || "", country_code: item.country_code || "",
        countryIso: item.countryIso || "IN", profile_url: item.profile_url || null,
        shift_from: item.shift_from || null, shift_to: item.shift_to || null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.shiftPickerOpen = false;
      this.shiftPickerValue = null;
      this.$validator.reset();
      this.serverSideErrors = {};
      this.guardDialog = true;
    },

    saveGuard() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        // Shift time validation
        // if (this.guard.shift_from && this.guard.shift_to) {
        //   const from = parseInt(this.guard.shift_from);
        //   const to = parseInt(this.guard.shift_to);
        //   if (from >= to) {
        //     this.setAlert({ showAlert: true, message: 'Shift start time must be less than shift end time', variant: 'red', timeout: 3000 });
        //     return;
        //   }
        // }
        this.saveLoading = true;
        const formData = new FormData();
        formData.append("name", this.guard.name);
        formData.append("mobileNo", this.guard.mobile_no);
        formData.append("email", this.guard.email || "");
        formData.append("country_code", this.guard.country_code);
        formData.append("countryIso", this.guard.countryIso);
        if (this.guard.shift_from) formData.append("shift_from", this.guard.shift_from);
        if (this.guard.shift_to)   formData.append("shift_to",   this.guard.shift_to);
        if (this.profile_pic) formData.append("profile_pic", this.profile_pic);

        this.$axios
          .post("/societyadmin/securityguard/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.getSecurityGuards();
              this.closeGuardDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    updateGuard() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        // Shift time validation
        // if (this.guard.shift_from && this.guard.shift_to) {
        //   const from = parseInt(this.guard.shift_from);
        //   const to = parseInt(this.guard.shift_to);
        //   if (from >= to) {
        //     this.setAlert({ showAlert: true, message: 'Shift start time must be less than shift end time', variant: 'red', timeout: 3000 });
        //     return;
        //   }
        // }
        this.saveLoading = true;
        const formData = new FormData();
        formData.append("name", this.guard.name);
        formData.append("mobileNo", this.guard.mobile_no);
        formData.append("email", this.guard.email || "");
        formData.append("country_code", this.guard.country_code);
        formData.append("countryIso", this.guard.countryIso);
        if (this.guard.shift_from) formData.append("shift_from", this.guard.shift_from);
        if (this.guard.shift_to)   formData.append("shift_to",   this.guard.shift_to);
        if (this.profile_pic) formData.append("profile_pic", this.profile_pic);

        this.$axios
          .put("/societyadmin/securityguard/" + this.guard.id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.getSecurityGuards();
              this.closeGuardDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    closeGuardDialog() {
      this.guardDialog = false;
      this.guard = {
        id: null, name: "", mobile_no: "", email: "",
        country_code: "+91", countryIso: "IN",
        profile_url: null, shift_from: null, shift_to: null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.shiftPickerOpen = false;
      this.shiftPickerValue = null;
      this.serverSideErrors = {};
      this.$validator.reset();
    },

   getSecurityGuards() {
  this.getRealSecurityGuards();
},



    formatTime12(time) {
      if (!time) return ''
      const [h, m] = time.split(':').map(Number)
      const ampm = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`
    },

    getRealSecurityGuards() {
      this.loading = true;
      const sortBy = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      const params = [
        `society_id=${this.user.id}`,
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`,
      ].join("&");

      this.$axios
        .get("societyadmin/securityguard?" + params)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.securityguards =
              data.securityguards.data || data.securityguards || [];
            this.totalRecords =
              data.total ||
              (data.securityguards && data.securityguards.total) ||
              0;
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteSecurityGuard() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/securityguard/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getSecurityGuards();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => {
          this.serverSideErrors[e.field] = e.message;
        });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },

    debounceSearch: debounce(function () {
      this.page = 1;
      this.getSecurityGuards();
    }, 500),
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    this.initFilters("securityguard");

    this.getSecurityGuards();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Table Cells ── */
.profile-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-avatar-wrap {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface2);
  border: 1px solid var(--border);
}
.p-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.p-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
}
.p-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.p-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}
.p-id {
  font-size: 10px;
  font-weight: 700;
  color: var(--tx4, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.mobile-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}
.email-cell {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tx3);
}
.shift-cell {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2);
  white-space: nowrap;
}

/* ── Phone Row (inline country code + mobile) ── */
.phone-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}
.mobile-input-wrap {
  flex: 1;
  min-width: 0;
}

/* ── Avatar Uploader ── */
.avatar-uploader-container {
  position: relative;
  display: inline-block;
}
.guard-avatar {
  background: var(--surface2) !important;
  border: 2px solid var(--border, rgba(0, 0, 0, 0.08)) !important;
}
.avatar-edit-btn {
  position: absolute !important;
  bottom: 2px !important;
  right: 2px !important;
  width: 38px !important;
  height: 38px !important;
  border: 2px solid white !important;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
  background-color: #757575 !important;
}

.avatar-edit-btn .v-icon {
  color: #000 !important;
}

.theme--dark .avatar-edit-btn .v-icon {
  color: #fff !important;
}

.avatar-edit-btn:hover {
  transform: scale(1.1);
}

.theme--dark .avatar-edit-btn {
  border-color: #111827 !important;
}

.theme--dark .avatar-edit-btn,
.theme--dark .avatar-border {
  border-color: var(--surface2, #1E1E1E) !important;
}
.theme--dark .avatar-edit-btn {
  background-color: #30384d !important;
}
/* ── Country Code Styles ── */
.field-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}
.custom-country-code-select-full {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}
.custom-country-code-select-full:hover {
  border-color: var(--brand) !important;
}
.theme--dark .custom-country-code-select-full {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* dropdown opens upward when near bottom of modal */
::v-deep .custom-country-code-select-full .dropdown-list {
  width: 260px !important;
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
  max-height: 200px;
  overflow-y: auto;
}

/* ── Shift Time ── */
.optional-label {
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: none;
  letter-spacing: 0;
}
.shift-time-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.shift-time-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 12px;
  border: 1px solid var(--border, rgba(0,0,0,0.1));
  border-radius: 8px;
  background: var(--surface, #fff);
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.shift-time-field:hover {
  border-color: var(--brand, #10b981);
}
.shift-time-val {
  flex: 1;
  font-size: 13.5px;
  font-weight: 500;
  color: #9ca3af;
}
.shift-time-val.has-val {
  color: var(--tx, #111827);
  font-weight: 600;
}
.shift-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #e5e7eb;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;
}
.shift-clear-btn:hover { background: #d1d5db; }

/* Shift Time Picker Dialog */
.shift-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--border, #e2e8f0);
  background: var(--surface, #fff);
}
.shift-dialog-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx, #111827);
}
.shift-picker-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.2s;
}
.shift-picker-close:hover { background: #e5e7eb; }
.shift-dialog-picker {
  border-radius: 0 !important;
  box-shadow: none !important;
}
.shift-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px 14px;
  border-top: 1px solid var(--border, #e2e8f0);
  background: var(--surface, #fff);
}
</style>

<style>
/* ── Security Guard shift time picker — dark mode ── */
.theme--dark .shift-dialog-picker .v-picker__body,
.theme--dark .shift-dialog-picker .v-time-picker-clock__container,
.theme--dark .inline-time-picker .v-picker__body,
.theme--dark .inline-time-picker .v-time-picker-clock__container {
  background: #273548 !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-clock,
.theme--dark .inline-time-picker .v-time-picker-clock {
  background: #1e293b !important;
}
.theme--dark .shift-dialog-picker .v-picker__title,
.theme--dark .inline-time-picker .v-picker__title {
  background: #1e293b !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-title__time span,
.theme--dark .shift-dialog-picker .v-time-picker-title__time .v-picker__title__btn,
.theme--dark .inline-time-picker .v-time-picker-title__time span,
.theme--dark .inline-time-picker .v-time-picker-title__time .v-picker__title__btn {
  color: #f1f5f9 !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-title__ampm .v-picker__title__btn,
.theme--dark .inline-time-picker .v-time-picker-title__ampm .v-picker__title__btn {
  color: rgba(255, 255, 255, 0.5) !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-title__ampm .v-picker__title__btn--active,
.theme--dark .inline-time-picker .v-time-picker-title__ampm .v-picker__title__btn--active {
  color: #ffffff !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-clock__item,
.theme--dark .inline-time-picker .v-time-picker-clock__item {
  color: #cbd5e1 !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-clock__hand,
.theme--dark .inline-time-picker .v-time-picker-clock__hand {
  background: #757575 !important;
}
/* Selected item dot — dark mode: use slate instead of bright color */
.theme--dark .shift-dialog-picker .v-time-picker-clock__item--active,
.theme--dark .inline-time-picker .v-time-picker-clock__item--active {
  background: #334155 !important;
  color: #f1f5f9 !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-clock__hand::before,
.theme--dark .inline-time-picker .v-time-picker-clock__hand::before {
  border-color: #475569 !important;
}
.theme--dark .shift-dialog-picker .v-time-picker-clock__hand::after,
.theme--dark .inline-time-picker .v-time-picker-clock__hand::after {
  background: #475569 !important;
}
/* Header + footer dark mode */
.theme--dark .shift-time-dialog .v-card {
  background: #273548  !important;
}
.theme--dark .shift-dialog-header {
  background: #273548  !important;
  border-bottom-color: rgba(255,255,255,0.08) !important;
}
.theme--dark .shift-dialog-title {
  color: #f1f5f9 !important;
}
.theme--dark .shift-picker-close {
  background: rgba(255,255,255,0.06) !important;
  border-color: rgba(255,255,255,0.12) !important;
}
.theme--dark .shift-picker-close:hover {
  background: rgba(255,255,255,0.12) !important;
}
.theme--dark .shift-dialog-actions {
  background: #273548  !important;
  border-top-color: rgba(255,255,255,0.08) !important;
}
</style>
